import React, { useState, useEffect, useCallback } from 'react';
import { 
  LayoutDashboard, 
  Coins, 
  Settings as SettingsIcon, 
  PlusCircle, 
  Search, 
  Menu, 
  Sun,
  Moon,
  User,
  ChevronDown,
  ChevronLeft,
  Globe,
  Zap
} from 'lucide-react';
import ItemList from './components/ItemList';
import ItemForm from './components/ItemForm';
import Dashboard from './components/Dashboard';
import MapView from './components/MapView';
import Notification from './components/Notification';
import BatchUpload from './components/BatchUpload';
import WorkerStatus from './components/WorkerStatus';
import Settings from './components/Settings';
import NumistaCallback from './components/NumistaCallback';
import AuthGate from './components/AuthGate';
import { getItems, getItem, deleteItem, getCurrentUser, getAuthToken, logoutUser, setAuthToken } from './services/api';

function App() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState(() => {
    const pathname = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '/';
    if (pathname.startsWith('/numista-callback')) return 'callback';
    if (pathname.startsWith('/catalog')) return 'catalog';
    if (pathname.startsWith('/add')) return 'add';
    if (pathname.startsWith('/edit')) return 'edit';
    if (pathname.startsWith('/map')) return 'map';
    return 'dashboard';
  });
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const initialFilters = {};
      const qType = params.get('type'); if (qType) initialFilters.type = qType;
      const qCountry = params.get('country'); if (qCountry) initialFilters.country = qCountry;
      const qYear = params.get('year'); if (qYear) initialFilters.year = qYear;
      const qSearch = params.get('search'); if (qSearch) initialFilters.search = qSearch;
      const qNumista = params.get('numista'); if (qNumista) initialFilters.numista = qNumista;
      return initialFilters;
    } catch (e) {
      return {};
    }
  });

  const [groupBy, setGroupBy] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qGroupBy = params.get('groupBy');
      return qGroupBy || 'none';
    } catch (e) {
      return 'none';
    }
  });
  const [notification, setNotification] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [settings, setSettings] = useState({ variantIncludeYear: true });
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        setAuthChecking(false);
        return;
      }

      // Keep cookie and localStorage in sync so protected image requests can authenticate.
      setAuthToken(token);

      try {
        const me = await getCurrentUser();
        setUser(me);
      } catch (err) {
        console.warn('Invalid auth session, forcing relogin', err);
        setAuthToken(null);
      } finally {
        setAuthChecking(false);
      }
    };

    initAuth();
  }, []);

  // Apply theme to document
  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [isDarkMode]);

  // Handle URL Routes — deep link ?item=<id> and /numista-callback
  useEffect(() => {
    // Initialize activeView from pathname for structural navigation
    const pathname = window.location.pathname || '/';
    if (pathname.startsWith('/numista-callback')) {
      setActiveView('callback');
      return;
    }
    if (pathname.startsWith('/catalog')) setActiveView('catalog');
    else if (pathname.startsWith('/add')) setActiveView('add');
    else if (pathname.startsWith('/edit')) setActiveView('edit');
    const params = new URLSearchParams(window.location.search);
    const itemId = params.get('item');
    if (itemId) {
      getItem(itemId)
        .then(data => {
          if (data?.item) {
            setEditingItem(data.item);
            setActiveView('edit');
          }
        })
        .catch(() => {
          window.history.replaceState(null, '', window.location.pathname);
        });
    }
    // Initialize filters and structural state from query string (persistent on refresh)
    const initialFilters = {};
    const qType = params.get('type'); if (qType) initialFilters.type = qType;
    const qCountry = params.get('country'); if (qCountry) initialFilters.country = qCountry;
    const qYear = params.get('year'); if (qYear) initialFilters.year = qYear;
    const qSearch = params.get('search'); if (qSearch) initialFilters.search = qSearch;
    const qNumista = params.get('numista'); if (qNumista) initialFilters.numista = qNumista;
    if (Object.keys(initialFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...initialFilters }));
    }
    const qView = params.get('view'); if (qView) setActiveView(qView);
    const qGroupBy = params.get('groupBy'); if (qGroupBy) setGroupBy(qGroupBy);
  }, []);

  // Prevent background scroll when mobile sidebar is open
  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileSidebarOpen]);

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Notification handler
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.warn('Logout request failed, cleaning local session anyway', err);
    } finally {
      setAuthToken(null);
      setUser(null);
      setIsProfileOpen(false);
      setActiveView('dashboard');
    }
  };

  // Load items
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getItems(filters);
      setItems(data.items || []);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load items');
      console.error(err);
      if(activeView === 'catalog') {
          showNotification('Failed to load items', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, activeView]);

  useEffect(() => {
    if (activeView === 'catalog') {
      loadItems();
    }
  }, [loadItems, activeView]);

  // Handle search/filter
  const handleSearch = (searchFilters) => {
    setFilters(prev => ({ ...prev, ...searchFilters }));
  };

  // Sync filters to URL so they persist on refresh and are shareable
  useEffect(() => {
    if (activeView !== 'catalog') return;
    const params = new URLSearchParams();
    if (filters.type) params.set('type', filters.type);
    if (filters.country) params.set('country', filters.country);
    if (filters.year) params.set('year', filters.year);
    if (filters.search) params.set('search', filters.search);
    if (filters.numista && filters.numista !== 'any') params.set('numista', filters.numista);
    if (groupBy && groupBy !== 'none') params.set('groupBy', groupBy);
    params.set('view', 'catalog');
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, [filters, activeView, groupBy]);

  // Navigation handlers
  const handleNavigate = (view) => {
    // Clear item deep-link param when navigating away from edit
    if (view !== 'edit') {
      const params = new URLSearchParams(window.location.search);
      if (params.has('item')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
    setActiveView(view);
    // Update URL pathname for structural navigation
    let path = '/';
    if (view === 'catalog') path = '/catalog';
    if (view === 'dashboard') path = '/';
    if (view === 'add') path = '/add';
    if (view === 'edit') path = '/edit';
    if (view === 'map') path = '/map';
    window.history.pushState(null, '', path + window.location.search);
    setIsProfileOpen(false);
    setIsMobileSidebarOpen(false);
    if (view === 'catalog') {
        setEditingItem(null);
        setError(null);
        loadItems();
    }
  };

  // Handle edit item
  const handleEdit = (item) => {
    setEditingItem(item);
    setActiveView('edit');
    if (item?.id) {
      window.history.pushState(null, '', `?item=${item.id}`);
    }
  };

  // Handle delete item
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteItem(id);
        showNotification('Item deleted successfully', 'success');
        loadItems();
      } catch (err) {
        showNotification('Failed to delete item: ' + (err.error || err), 'error');
      }
    }
  };

  // Handle form close
  const handleFormClose = (saved) => {
    window.history.replaceState(null, '', window.location.pathname);
    if (saved) {
      showNotification(editingItem ? 'Item updated successfully' : 'Item added successfully', 'success');
      handleNavigate('catalog');
    } else {
      handleNavigate('catalog');
    }
  };

  const themeClasses = isDarkMode 
    ? {
        bg: 'bg-[#0F171A]',
        sidebar: 'bg-[#0A1012] border-slate-800',
        header: 'bg-[#0F171A]/80 border-slate-800',
        card: 'bg-[#162125] border-slate-800',
        textMain: 'text-slate-200',
        textMuted: 'text-slate-500',
        input: 'bg-[#1A2E35] border-slate-700',
        hover: 'hover:bg-[#1A2E35]',
        accent: 'text-[#4FD1C5]',
        accentBg: 'bg-[#4FD1C5]'
      }
    : {
        bg: 'bg-[#F4F4F0]',
        sidebar: 'bg-[#FFFFFF] border-[#E2E2D9]',
        header: 'bg-[#F4F4F0]/80 border-[#E2E2D9]',
        card: 'bg-[#FFFFFF] border-[#E2E2D9]',
        textMain: 'text-[#2D3748]',
        textMuted: 'text-[#718096]',
        input: 'bg-[#E2E2D9]/50 border-[#D1D1C7]',
        hover: 'hover:bg-[#F4F4F0]',
        accent: 'text-[#2D6A4F]',
        accentBg: 'bg-[#2D6A4F]'
      };

  const SidebarItem = ({ icon: Icon, label, id }) => (
    <button
      onClick={() => handleNavigate(id)}
      className={`relative flex items-center w-full p-3 transition-all duration-200 border-l-2 ${
        activeView === id 
        ? `${isDarkMode ? 'bg-[#1A2E35]' : 'bg-[#F4F4F0]'} border-current ${themeClasses.accent}` 
        : `border-transparent ${themeClasses.textMuted} hover:${isDarkMode ? 'text-slate-100' : 'text-slate-800'} ${themeClasses.hover}`
      }`}
    >
      <Icon size={18} className="shrink-0" />
      {!isSidebarVisuallyCollapsed && (
        <span className="ml-3 text-xs font-bold tracking-[0.2em] uppercase whitespace-nowrap overflow-hidden">
          {label}
        </span>
      )}
      {activeView === id && !isSidebarVisuallyCollapsed && (
        <div className={`absolute right-4 w-1 h-1 ${themeClasses.accentBg} rotate-45`} />
      )}
    </button>
  );

  // Keep activeView persisted to URL when navigating
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('view', activeView);
    const qs = params.toString();
    const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, [activeView]);

  const getPageTitle = () => {
    switch(activeView) {
      case 'dashboard': return 'Overview';
      case 'catalog': return 'Catalogo';
      case 'add': return 'Inserisci';
      case 'edit': return 'Modifica';
      case 'batch': return 'Batch_Upload';
      case 'map': return 'Mappa';
      case 'settings': return 'Sistema';
      default: return 'Storage_Unit';
    }
  };

  const getUserInitials = () => {
    const source = (user?.username || user?.email || '').trim();
    if (!source) return 'MC';

    const pieces = source
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean);

    if (pieces.length >= 2) {
      return `${pieces[0][0]}${pieces[1][0]}`.toUpperCase();
    }

    if (pieces.length === 1) {
      return pieces[0].slice(0, 2).toUpperCase();
    }

    return 'MC';
  };

  const isSidebarVisuallyCollapsed = isSidebarCollapsed && !isMobileSidebarOpen;

  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#0F171A] text-slate-200 flex items-center justify-center">
        Verifica sessione...
      </div>
    );
  }

  if (!user) {
    return <AuthGate onAuthenticated={setUser} />;
  }

  return (
    <div className={`flex min-h-screen ${themeClasses.bg} ${themeClasses.textMain} font-sans transition-colors duration-300 relative overflow-hidden`}>
      
      {/* Background Kanji Watermark */}
      <div 
        className={`fixed -bottom-20 -right-20 pointer-events-none select-none z-0 opacity-[0.03] font-black leading-none text-[400px] transition-all duration-1000 ease-in-out`}
        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
      >
        {activeView === 'dashboard' ? '金' : 
         activeView === 'catalog' ? '寶' : 
         (activeView === 'add' || activeView === 'edit') ? '求' : 
         activeView === 'batch' ? '速' : 
         activeView === 'map' ? '地' : 
         activeView === 'settings' ? '道' : '金'}
      </div>

      <Notification 
        message={notification?.message} 
        type={notification?.type} 
        onClose={closeNotification} 
      />

      <WorkerStatus />

      {/* Mobile sidebar overlay */}
      {isMobileSidebarOpen && (
        <button
          type="button"
          aria-label="Chiudi menu laterale"
          onClick={() => setIsMobileSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/45 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 ${isSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'} ${themeClasses.sidebar} border-r transition-all duration-300 transform ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static`}>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="p-4 h-20 flex items-center justify-between">
            {!isSidebarVisuallyCollapsed && (
              <div className="flex items-center space-x-3 overflow-hidden animate-in fade-in duration-500">
                <div className={`w-8 h-8 border ${isDarkMode ? 'border-[#4FD1C5]' : 'border-[#2D6A4F]'} flex items-center justify-center ${themeClasses.accent} font-light text-lg`}>
                  K
                </div>
                <div>
                  <h1 className={`text-sm font-bold tracking-[0.2em] ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>KOZENI</h1>
                  <p className={`text-[8px] ${themeClasses.accent} tracking-[0.2em] font-light mt-[-2px]`}>小銭 / DB</p>
                </div>
              </div>
            )}
            <button 
              onClick={() => {
                if (window.innerWidth < 1024) {
                  setIsMobileSidebarOpen(false);
                } else {
                  setIsSidebarCollapsed(!isSidebarCollapsed);
                }
              }}
              className={`p-1 ${themeClasses.textMuted} hover:text-current transition-colors mx-auto lg:mx-0`}
            >
              {isMobileSidebarOpen ? <ChevronLeft size={20} /> : (isSidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />)}
            </button>
          </div>

          <nav className="flex-1 mt-6 overflow-y-auto">
            <SidebarItem icon={LayoutDashboard} label="Dashboard" id="dashboard" />
            <SidebarItem icon={Coins} label="Catalogo" id="catalog" />
            <SidebarItem icon={PlusCircle} label="Inserisci" id="add" />
            <SidebarItem icon={Zap} label="Batch" id="batch" />
            <SidebarItem icon={Globe} label="Mappa" id="map" />
          </nav>

          <div className="p-4 flex-shrink-0">
            <div className={`text-[40px] font-bold opacity-5 pointer-events-none select-none leading-none mb-4 ${isSidebarVisuallyCollapsed ? 'hidden' : 'block'}`}>小銭</div>
            <SidebarItem icon={SettingsIcon} label="Sistema" id="settings" />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative">
        {/* Header */}
        {activeView !== 'map' && (
          <header className={`sticky top-0 z-30 ${themeClasses.header} backdrop-blur-md border-b p-4 lg:px-8 px-4 flex items-center justify-between transition-colors duration-300`}>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(true)}
                className={`lg:hidden p-2 rounded-sm border ${themeClasses.card} ${themeClasses.textMuted} hover:text-current transition-all`}
                aria-label="Apri menu"
              >
                <Menu size={16} />
              </button>
              <h2 className={`text-[10px] tracking-[0.5em] font-bold ${themeClasses.accent} uppercase`}>
                {getPageTitle()}
              </h2>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Search (Hide on small) */}
              {activeView === 'catalog' && (
                <div className={`hidden md:flex items-center ${themeClasses.input} border px-3 py-1.5 rounded-sm`}>
                  <Search className={themeClasses.textMuted} size={14} />
                  <input 
                    type="text" 
                    placeholder="DATABASE SEARCH..." 
                    className="bg-transparent border-none focus:outline-none text-[9px] tracking-widest ml-3 w-40"
                    value={filters.search || ''}
                    onChange={(e) => handleSearch({ search: e.target.value })}
                  />
                  {filters.search && (
                    <button
                      type="button"
                      onClick={() => handleSearch({ search: '' })}
                      className={`ml-1 ${themeClasses.textMuted} hover:text-current transition-colors text-xs leading-none`}
                      aria-label="Cancella ricerca testuale"
                      title="Cancella"
                    >
                      x
                    </button>
                  )}
                  <select
                    value={filters.numista || 'any'}
                    onChange={(e) => handleSearch({ numista: e.target.value })}
                    className="ml-3 text-[9px] px-2 py-1 border bg-transparent tracking-widest rounded-sm"
                  >
                    <option value="any">Tutte</option>
                    <option value="linked">Collegate a Numista</option>
                    <option value="unlinked">Non collegate</option>
                  </select>
                </div>
              )}

              {/* Theme Toggle */}
              <button 
                onClick={toggleTheme}
                className={`p-2 rounded-sm border ${themeClasses.card} ${themeClasses.textMuted} hover:text-current transition-all`}
              >
                {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              {/* Profile Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`flex items-center space-x-3 p-1 pr-3 rounded-sm border ${isProfileOpen ? themeClasses.accent + ' border-current' : themeClasses.card} transition-all`}
                >
                  <div className={`w-8 h-8 ${themeClasses.accentBg} text-white flex items-center justify-center font-bold text-xs rounded-sm`}>
                    {getUserInitials()}
                  </div>
                  <ChevronDown size={14} className={`${isProfileOpen ? 'rotate-180' : ''} transition-transform ${themeClasses.textMuted}`} />
                </button>

                {isProfileOpen && (
                  <div className={`absolute right-0 mt-2 w-48 ${themeClasses.card} border shadow-xl rounded-sm py-2 z-50 animate-in fade-in zoom-in-95 duration-200`}>
                    <div className="px-4 py-2 border-b border-inherit mb-2">
                      <p className="text-[10px] tracking-widest opacity-50 uppercase">Utente</p>
                      <p className="text-xs font-bold">{user.username}</p>
                    </div>
                    <button 
                      onClick={() => handleNavigate('settings')}
                      className={`w-full text-left px-4 py-2 text-xs flex items-center space-x-2 ${themeClasses.hover}`}
                    >
                      <User size={14} /> <span>Profilo</span>
                    </button>
                    <button 
                      onClick={() => handleNavigate('settings')}
                      className={`w-full text-left px-4 py-2 text-xs flex items-center space-x-2 ${themeClasses.hover}`}
                    >
                      <SettingsIcon size={14} /> <span>Preferenze</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className={`w-full text-left px-4 py-2 text-xs flex items-center space-x-2 text-red-400 ${themeClasses.hover}`}
                    >
                      <User size={14} /> <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Content Area */}
        <div className={`p-6 lg:p-10 max-w-7xl mx-auto w-full transition-all duration-500 ${activeView === 'map' ? 'p-0 max-w-full h-screen' : ''}`}>
          {activeView === 'dashboard' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-700">
              <Dashboard themeClasses={themeClasses} isDarkMode={isDarkMode} />
            </div>
          )}

          {activeView === 'map' && <MapView />}

          {activeView === 'batch' && <BatchUpload onNotification={showNotification} themeClasses={themeClasses} isDarkMode={isDarkMode} />}
          
          {activeView === 'settings' && <Settings settings={settings} onUpdateSettings={setSettings} themeClasses={themeClasses} isDarkMode={isDarkMode} />}

          {activeView === 'callback' && <NumistaCallback />}

          {activeView === 'catalog' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              <div className="flex justify-between items-center mb-8 border-b border-inherit pb-6 transition-colors">
                <h3 className="text-[10px] tracking-[0.5em] font-bold uppercase">Archivio_Centrale</h3>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleNavigate('add')}
                    className={`px-4 py-2 ${themeClasses.accentBg} text-white text-[9px] font-bold tracking-widest uppercase shadow-lg shadow-current/10`}
                  >
                    Aggiungi_Entry
                  </button>
                </div>
              </div>
              
              {loading && <div className={`text-center py-10 ${themeClasses.textMuted}`}>Loading collection...</div>}
              {error && <div className="text-center py-10 text-red-500">{error}</div>}
              
              {!loading && !error && (
                <ItemList 
                  items={items}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  settings={settings}
                  themeClasses={themeClasses}
                  isDarkMode={isDarkMode}
                  groupBy={groupBy}
                  onGroupByChange={(g) => setGroupBy(g)}
                />
              )}
            </div>
          )}

          {(activeView === 'add' || activeView === 'edit') && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              <ItemForm 
                item={editingItem}
                onClose={handleFormClose}
                onNotification={showNotification}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;

